import { forwardRef, type Ref } from "react";
import { ImageViewer } from "./image-viewer";
import { VideoPlayer, type VideoPlayerHandle } from "./video-player";
import type { WatchComment } from "@/hooks/use-watch";

/**
 * MediaSurface (2026-07-18) — asset-type dispatch for the watch page.
 *
 * The three supported assetTypes each pick a different underlying player, but
 * from the shell's perspective they all expose the same VideoPlayerHandle
 * (seek / getCurrentTime / isPlaying / pause). Image degrades the time-related
 * methods to no-ops so the composer + right-panel can call them uniformly.
 *
 *   video  → VideoPlayer
 *   audio  → VideoPlayer with `assetType="audio"` (adds decorative SVG bars)
 *   image  → ImageViewer (no timeline, no playback controls, zoom in bottom bar)
 *
 * Backend field `videos.assetType` is the source of truth; it's already stamped
 * per version by the upload flow's MIME allowlist (Task 11 / [[project-versions-model]]).
 */

type Props = {
  assetType: "video" | "audio" | "image" | undefined;
  src: string | null | undefined;
  sourceType?: "upload" | "youtube" | "vimeo";
  sourceVideoId?: string | null;
  sourceUrl?: string | null;
  posterLabel?: string;
  comments?: WatchComment[];
  mode?: "workspace" | "guest" | "share";
  watchKey?: string;
  currentVersionId?: string | null;
  selectedCommentId?: string | null;
  onSelectComment?: (id: string) => void;
};

export const MediaSurface = forwardRef(function MediaSurface(
  {
    assetType,
    src,
    sourceType,
    sourceVideoId,
    sourceUrl,
    posterLabel,
    comments,
    mode,
    watchKey,
    currentVersionId,
    selectedCommentId,
    onSelectComment,
  }: Props,
  ref: Ref<VideoPlayerHandle>,
) {
  // Unknown assetType (e.g. old rows missing the column) → treat as video.
  // The video element still handles audio-only sources fine (audio just shows
  // no picture in that fallback case — one of the reasons Task 14 landed).
  if (assetType === "image") {
    return (
      <ImageViewer
        ref={ref}
        src={src}
        posterLabel={posterLabel}
        comments={comments}
        mode={mode}
        watchKey={watchKey}
        currentVersionId={currentVersionId}
      />
    );
  }

  return (
    <VideoPlayer
      ref={ref}
      src={src}
      sourceType={sourceType}
      sourceVideoId={sourceVideoId}
      sourceUrl={sourceUrl}
      posterLabel={posterLabel}
      comments={comments}
      mode={mode}
      watchKey={watchKey}
      currentVersionId={currentVersionId}
      assetType={assetType === "audio" ? "audio" : "video"}
      selectedCommentId={selectedCommentId}
      onSelectComment={onSelectComment}
    />
  );
});
